import { ChangeEvent } from "react";

import {
  Button,
  Checkbox,
  Icon,
  InfoCircleHollowSVG,
  TooltipDialog,
} from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";

import {
  filterAndTooltipContainerCss,
  subHeadingCss,
  tooltipDialogCss,
  tooltipIconCss,
} from "@/search/NewSearchPageMVP/FilterPanel/filters/ReadingLevels/ReadingLevels.styles";
import { readingSkillCheckboxCss } from "@/search/NewSearchPageMVP/FilterPanel/filters/ReadingSkills/ReadingSkills.styles";
import { useSearchAdminPermissions } from "@/search/NewSearchPageMVP/provider/hooks";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";

const CheckboxWithTrackEvent = withTrackEvent(
  Checkbox,
) as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof Checkbox> & React.RefAttributes<HTMLInputElement>
>;

const getEventProperties = (filterName: string, isToggled: boolean) => ({
  objectType: objectTypes.content,
  actionPrefix: actionPrefixes.initiate,
  actionName: actionNames.filter,
  streamProperties: {
    description: `Toggle content maturity ${filterName} search filter ${isToggled ? "off" : "on"}`,
  },
});

const MaturityLevels = () => {
  const {
    getters: { validQueryParams },
    handlers: { hasSearchParamValue, toggleSearchParamValue },
  } = useSearchPageContext();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  const maturityFilterOptions =
    validQueryParams?.[AgathaSchemaParamsEnum.content_maturities]?.items
      ?.values;

  if (!maturityFilterOptions) return null;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    toggleSearchParamValue(
      AgathaSchemaParamsEnum.content_maturities,
      e.target.value,
    );
  };

  return (
    <>
      <div css={filterAndTooltipContainerCss}>
        <h3 css={subHeadingCss}>Maturity level</h3>
        <TooltipDialog
          offset={8}
          placement="bottom-start"
          header="Maturity level"
          body={
            <>
              This filters the maturity of the content.
              <br />
              <br />
              For example, an article about the summer olympics would have an
              Upper Elementary School – Middle School rating.
            </>
          }
          buttonProps={{
            flavor: Button.flavors.incognito,
          }}
          ariaProps={{
            "aria-label": "Maturity Level Filter Tooltip",
          }}
          __cssFor={tooltipDialogCss}
          buttonContents={
            <Icon
              SvgComponent={InfoCircleHollowSVG}
              __cssFor={tooltipIconCss}
            />
          }
        />
      </div>

      {maturityFilterOptions.map(({ label, value }) => (
        <CheckboxWithTrackEvent
          onChange={handleChange}
          key={value}
          label={label}
          value={value}
          checked={hasSearchParamValue(
            AgathaSchemaParamsEnum.content_maturities,
            value,
          )}
          __cssFor={readingSkillCheckboxCss}
          dataProps={{
            "data-qa-selector": `select-${value.replace(" ", "-")}-search-filter`,
          }}
          {...getEventProperties(
            label,
            hasSearchParamValue(
              AgathaSchemaParamsEnum.content_maturities,
              value,
            ),
          )}
        />
      ))}
    </>
  );
};

MaturityLevels.displayName = "MaturityLevels";
export { MaturityLevels };
